import React from "react";
import styled from "styled-components";
import { Card, Row, Col, Statistic, Tag } from "antd";
import {
  WarningOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const HealthStripContainer = styled.div`
  margin-bottom: 24px;
`;

const HealthCard = styled(Card)`
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .ant-card-body {
    padding: 16px;
  }
`;

const HealthCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
`;

const ValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const SeverityBadge = styled(Tag)`
  margin: 0;
  font-weight: 600;
`;

const OperationalHealthStrip = ({ operationalHealth, onNavigate }) => {
  const navigate = useNavigate();

  const handleClick = (type, filter) => {
    if (onNavigate) {
      onNavigate(type, filter);
    } else {
      // Default navigation
      if (type === "incidents") {
        navigate("/orders?filter=incidents");
      } else if (type === "exceptions") {
        navigate("/orders?filter=exceptions");
      } else if (type === "payouts") {
        navigate("/finance");
      } else if (type === "trust") {
        navigate("/intelligence/governance");
      }
    }
  };

  const incidents = operationalHealth?.incidents || {
    high: 0,
    medium: 0,
    critical: 0,
    totalOpen: 0,
  };
  const exceptions = operationalHealth?.exceptions || { open: 0 };
  const payouts = operationalHealth?.payouts || { heldAmount: 0, currency: "ZAR" };
  const trust = operationalHealth?.trust || { alerts: 0 };

  return (
    <HealthStripContainer>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <HealthCard
            onClick={() => handleClick("incidents")}
            style={{
              borderLeft: `4px solid ${
                incidents.totalOpen > 0 ? "#ff4d4f" : "#52c41a"
              }`,
            }}
          >
            <HealthCardTitle>
              <WarningOutlined style={{ color: "#ff4d4f" }} />
              Active Incidents
            </HealthCardTitle>
            <ValueContainer>
              <Statistic
                value={incidents.totalOpen}
                valueStyle={{
                  fontSize: "24px",
                  fontWeight: 600,
                  color: incidents.totalOpen > 0 ? "#ff4d4f" : "#52c41a",
                }}
              />
              {(incidents.high > 0 || incidents.medium > 0) && (
                <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
                  {incidents.high > 0 && (
                    <SeverityBadge color="error">{incidents.high} High</SeverityBadge>
                  )}
                  {incidents.medium > 0 && (
                    <SeverityBadge color="warning">{incidents.medium} Medium</SeverityBadge>
                  )}
                </div>
              )}
            </ValueContainer>
          </HealthCard>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <HealthCard
            onClick={() => handleClick("exceptions")}
            style={{
              borderLeft: `4px solid ${
                exceptions.open > 0 ? "#faad14" : "#52c41a"
              }`,
            }}
          >
            <HealthCardTitle>
              <ExclamationCircleOutlined style={{ color: "#faad14" }} />
              Open Exceptions
            </HealthCardTitle>
            <Statistic
              value={exceptions.open}
              valueStyle={{
                fontSize: "24px",
                fontWeight: 600,
                color: exceptions.open > 0 ? "#faad14" : "#52c41a",
              }}
            />
          </HealthCard>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <HealthCard
            onClick={() => handleClick("payouts")}
            style={{
              borderLeft: `4px solid ${
                payouts.heldAmount > 0 ? "#ff4d4f" : "#52c41a"
              }`,
            }}
          >
            <HealthCardTitle>
              <DollarOutlined style={{ color: "#ff4d4f" }} />
              Held / At-Risk Payouts
            </HealthCardTitle>
            <Statistic
              value={payouts.heldAmount}
              prefix="R"
              valueStyle={{
                fontSize: "24px",
                fontWeight: 600,
                color: payouts.heldAmount > 0 ? "#ff4d4f" : "#52c41a",
              }}
            />
          </HealthCard>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <HealthCard
            onClick={() => handleClick("trust")}
            style={{
              borderLeft: `4px solid ${
                trust.alerts > 0 ? "#ff4d4f" : "#52c41a"
              }`,
            }}
          >
            <HealthCardTitle>
              <SafetyOutlined style={{ color: "#1890ff" }} />
              Trust Alerts
            </HealthCardTitle>
            <Statistic
              value={trust.alerts}
              valueStyle={{
                fontSize: "24px",
                fontWeight: 600,
                color: trust.alerts > 0 ? "#ff4d4f" : "#52c41a",
              }}
            />
          </HealthCard>
        </Col>
      </Row>
    </HealthStripContainer>
  );
};

export default OperationalHealthStrip;
